// Iter 386 — Asignar familia/subfamilia a los ítems sin cuenta de la OC asociada a una bill en 5124.
// Queda guardado en la OC (auditado) y re-propone todas las bills de esa OC.
import { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { SearchableCombobox } from '../../components/ui/searchable-combobox';
import { Loader2, Save, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const fmtCLP = (n) => `$${Math.round(n || 0).toLocaleString('es-CL')}`;

export function AsignarFamiliasDialog({ propuesta, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [subfamilias, setSubfamilias] = useState([]);
  const [sel, setSel] = useState({});
  const [masivo, setMasivo] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!propuesta) return;
    setData(null); setSel({}); setMasivo(''); setLoading(true);
    Promise.all([
      api.get(`/contabilidad/cuentas-alegra/recat/propuestas/${propuesta.alegra_id}/items-oc`),
      api.get('/bodega/subfamilias-lista'),
    ]).then(([r, s]) => {
      setData(r.data);
      setSubfamilias((s.data.subfamilias || []).filter((sf) => sf.cta_alegra));
    }).catch((err) => {
      toast.error(err?.response?.data?.detail || 'No se pudieron cargar los ítems de la OC');
      onClose();
    }).finally(() => setLoading(false));
  }, [propuesta, onClose]);

  const sfItems = useMemo(
    () => subfamilias.map((sf) => ({ value: sf.codpf, label: `${sf.familia} › ${sf.subfamilia}`, subLabel: sf.concepto })),
    [subfamilias],
  );
  const cambios = useMemo(
    () => Object.entries(sel).filter(([, v]) => v).map(([item, codpf]) => ({ item: Number(item), subfamilia_codpf: codpf })),
    [sel],
  );
  const sinCuenta = (data?.items || []).filter((it) => !it.cuenta_id).length;

  const guardar = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/contabilidad/cuentas-alegra/recat/propuestas/${propuesta.alegra_id}/familias`, { asignaciones: cambios });
      if (r.data?.errores?.length) toast.warning(r.data.errores.join(' · '));
      toast.success(`OC ${r.data.oc}: ${r.data.items_actualizados} ítem(s) con familia · ${r.data.propuestas?.length || 0} bill(s) re-propuestas — ejecuta para aplicar en Alegra`);
      onSaved(r.data.propuestas || []);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar la familia');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!propuesta} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="dialog-asignar-familias">
        <DialogHeader>
          <DialogTitle className="text-base">
            Familia de los ítems · OC {data?.oc || propuesta?.oc} · bill {propuesta?.folio} — {propuesta?.proveedor}
          </DialogTitle>
          <DialogDescription className="text-xs">
            La cuenta contable sale de la familia del ítem. Asigna la subfamilia a los ítems sin cuenta: queda guardada en la OC
            {data?.bills_misma_oc > 1 ? ` y se re-proponen las ${data.bills_misma_oc} bills de esta OC` : ' y se re-propone la bill'}.
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Cargando ítems…</div>}

        {data && (
          <>
            {sinCuenta > 1 && (
              <div className="flex items-center gap-2 text-xs bg-slate-50 border rounded p-2" data-testid="familias-masivo">
                <span className="text-muted-foreground whitespace-nowrap">Misma familia para los {sinCuenta} ítems sin cuenta:</span>
                <div className="flex-1 min-w-[260px]">
                  <SearchableCombobox compact items={sfItems} value={masivo} onChange={(s) => setMasivo(s ? String(s.value) : '')}
                    placeholder="Seleccionar familia…" testid="combo-familia-masivo" />
                </div>
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!masivo} data-testid="btn-aplicar-masivo"
                  onClick={() => setSel((prev) => ({ ...prev, ...Object.fromEntries(data.items.filter((it) => !it.cuenta_id).map((it) => [it.item, masivo])) }))}>
                  Aplicar a todos
                </Button>
              </div>
            )}
          <div className="border rounded max-h-[50vh] overflow-y-auto" data-testid="tabla-items-oc">
            <table className="w-full text-xs">
              <thead className="bg-muted sticky top-0 z-10">
                <tr>
                  <th className="text-left px-2 py-1.5 w-8">#</th>
                  <th className="text-left px-2 py-1.5">Ítem de la OC</th>
                  <th className="text-right px-2 py-1.5">Total</th>
                  <th className="text-left px-2 py-1.5 min-w-[280px]">Cuenta / familia</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it) => {
                  const editable = !it.cuenta_id || it.fuente === 'subfamilia asignada';
                  return (
                    <tr key={it.item} className={`border-t ${!it.cuenta_id ? 'bg-red-50/50' : ''}`} data-testid={`item-oc-${it.item}`}>
                      <td className="px-2 py-1 font-mono">{it.item}</td>
                      <td className="px-2 py-1">
                        <div className="truncate max-w-[360px]" title={it.descripcion}>{it.descripcion}</div>
                        {it.codigo_material && <div className="text-[10px] text-muted-foreground font-mono">{it.codigo_material}</div>}
                      </td>
                      <td className="px-2 py-1 text-right font-mono whitespace-nowrap">{fmtCLP(it.total_linea)}</td>
                      <td className="px-2 py-1">
                        {editable ? (
                          <div className={!it.cuenta_id && !sel[it.item] ? 'rounded ring-1 ring-red-400' : ''}>
                            <SearchableCombobox compact items={sfItems}
                              value={sel[it.item] ?? it.subfamilia_codpf ?? ''}
                              onChange={(s) => setSel((prev) => ({ ...prev, [it.item]: s ? String(s.value) : '' }))}
                              placeholder={it.cuenta_id ? it.cuenta_nombre : 'Sin familia — seleccionar…'}
                              testid={`combo-familia-${it.item}`} />
                          </div>
                        ) : (
                          <div>
                            <span className="text-emerald-700">{it.cuenta_nombre}</span>
                            <span className="text-[10px] text-muted-foreground ml-1">· {it.fuente}</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}

        <DialogFooter className="items-center gap-2 sm:justify-between">
          <span className="text-xs text-muted-foreground flex items-center gap-1" data-testid="familias-resumen">
            {sinCuenta > 0 && <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />}
            {sinCuenta} ítem(s) sin cuenta · {cambios.length} por guardar
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={saving} data-testid="btn-cancelar-familias">Cancelar</Button>
            <Button size="sm" onClick={guardar} disabled={saving || !cambios.length} data-testid="btn-guardar-familias">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              Guardar y re-proponer
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
